import React, { useState, useEffect, useRef, useCallback } from "react";
import { Input, InputBox, SubmitButton, UploadFile, UploadFileCheckIcon, VipUploadButton, VipUploadButtonText } from "../chatStyled";

interface InputMessageProps {
    Placeholder: string;
    SendMessageIcon: React.ReactElement;
    FileToUpload?: File | null;
    UserVideoNum?: number;
    CurrentMessage: string;
    setCurrentMessage: (message: string) => void;
    onSend?: (message: string) => void;
    removeFileToUpload?: () => void;
    isSending?: boolean;
    isBlocked?: boolean;
    onBlockedAction?: () => void;
    isVipCoachPage?: boolean;
    isCoach?: boolean;
    onLoadFile?: (file: File) => void;
}

export const InputMessage: React.FC<InputMessageProps> = (props) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (props.isBlocked) {
            props.onBlockedAction?.();
            return;
        }
        if (!props.CurrentMessage.trim() && !props.FileToUpload) return;
        if (props.isSending) return;
        props.onSend?.(props.CurrentMessage || '');
        props.setCurrentMessage('');
        inputRef.current?.focus();
    };
    
    const handleInputClick = useCallback(() => {
        if (props.isBlocked) {
            props.onBlockedAction?.();
            return;
        }
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [props.isBlocked, props.onBlockedAction]);

    const handleUploadClick = useCallback(() => {
        if (props.isBlocked) {
            props.onBlockedAction?.();
            return;
        }
        fileInputRef.current?.click();
    }, [props.isBlocked, props.onBlockedAction]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            props.onLoadFile?.(file);
            e.target.value = '';
        }
    };
    
    // Focus the input on mount
    useEffect(() => {
        if (!props.isBlocked) {
            inputRef.current?.focus();
        }
    }, []);

    // Expose handleInputClick on window so ConnectAccordion can call it directly
    useEffect(() => {
        (window as any).__inputMessageHandleClick = handleInputClick;
        return () => {
            delete (window as any).__inputMessageHandleClick;
        };
    }, [handleInputClick]);
    
    return(
        <InputBox as="form" onSubmit={handleSubmit} $isVipPage={props.isVipCoachPage}>
            {props.isVipCoachPage && (
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*,image/*"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                    <VipUploadButton onClick={handleUploadClick}>
                        <img
                            src="https://members.performancegolf.com/wp-content/themes/pgm/dist/images/icons/attach-new.svg"
                            alt=""
                            aria-hidden="true"
                        />
                        <VipUploadButtonText>Upload Swing Video</VipUploadButtonText>
                    </VipUploadButton>
                </>
            )}
            <Input
                ref={inputRef}
                placeholder={props.Placeholder}
                value={props.CurrentMessage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.setCurrentMessage?.(e.target.value)}
                disabled={props.isSending}
                readOnly={props.isBlocked}
                onClick={handleInputClick}
            />
            <SubmitButton 
                type="submit" 
                color="primary" 
                size="large" 
                aria-label="directions"
                disabled={props.isSending}
                $isVipPage={props.isVipCoachPage}
            >
                {props.SendMessageIcon}
            </SubmitButton>
            {props.FileToUpload &&
                <UploadFile>
                    <UploadFileCheckIcon className="bi bi-check" />
                    <span>{props.FileToUpload.type.startsWith('image/') ? 'Image' : (props.isVipCoachPage && !props.isCoach ? `Swing ${props.UserVideoNum}` : `Fix ${props.UserVideoNum}`)}</span>
                    <span onClick={props.removeFileToUpload}>Remove</span>
                </UploadFile>}
        </InputBox>
    );
}

export default InputMessage;